import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  ApiError,
  Bootstrap,
  CapabilityState,
  MobileMode,
  onSessionExpired,
  Profile,
  PublicUser,
  UserPositionView,
} from './api';
import { buildRoleIndex, byCode, RoleIndex } from './lib/kb';
import { Login } from './auth/Login';
import { PinSetup } from './auth/PinSetup';
import { Shell } from './shell/Shell';
import { ConfirmHost } from './components/Confirm';
import { forgetChatDevice } from './chat/ChatProvider';
import { InviteAccept } from './auth/InviteAccept';
import { GuestRoom } from './rooms/GuestRoom';
import { GuestContract } from './contracts/GuestContract';
import { GuestPass } from './guest/GuestPass';
import { forgetPinLogin, pinOfferSkipped } from './lib/pin';
import { watchIdle } from './lib/idle';
import { isApp } from './native/env';
import { AppLock } from './native/AppLock';
import { onTabs, tellTabs, type OutReason } from './lib/tabs';
import {
  cardLayer, checkLayer, contractStack, eventLayer, kbPageLayer, legalCardLayer, legalFundLayer, newDealLayer, procLayer, projectLayer, registerLayer, subLayer,
  type CalendarLayer, type ContractTab, type KbPage, type ProjectsLayer, type ProjSection, type Section,
} from './nav/routes';
import { initNav, useNav } from './nav/nav';
import type { DealPreset } from './contracts/purpose';
import { accessLevel, seesAllOfFunction, type AccessLevel } from './lib/access';
import { FUNCTION_ORDER_IN_MENU, FUNCTION_SECTION, type FunctionName } from './functions/functions';
import { mobileModules, modeOf } from './nav/mobile';

/** Бездействие дольше этого — автоматический выход из приложения. */
const IDLE_LOGOUT_MS = 15 * 60_000;

/** Надпись на окне входа по причине выхода; у кнопки «Выйти» надписи нет. */
const OUT_NOTICE: Record<Exclude<OutReason, null>, string> = {
  idle: 'Вы вышли автоматически: 15 минут без действий.',
  expired: 'Сессия истекла — войдите снова.',
  disabled: 'Доступ закрыт администратором.',
  password: 'Пароль изменён — войдите снова.',
};

export type { AccessLevel };

/**
 * Столько ждём базу знаний молча. Дальше запрос уже пошёл на повторы, и
 * «Загружаю…» перестаёт быть правдой: загрузка не идёт, она не удаётся.
 */
const BOOT_SLOW_MS = 4_000;

// replace, а не assign: «назад» не должно возвращать на погашенную ссылку.
// Константа модуля — иначе эффект страницы ссылки перезапускался бы на каждой отрисовке.
const goReplace = (url: string) => window.location.replace(url);

/** Раздел приложения — то, что стоит первым сегментом адреса (nav/routes.ts). */
export type View = Section;
export type { ProjSection } from './nav/routes';

export type AppCtx = {
  user: PublicUser;
  /** Должности сотрудника: основная первой, замещения со сроком. */
  positions: UserPositionView[];
  /** Есть ли у сотрудника доступ. */
  can: (permission: string) => boolean;
  /** Номинирован ли в команду конкретного проекта. */
  isMemberOf: (projectId: string) => boolean;
  /** Мастер-наблюдатель без номинации: всё видно, ничего нельзя менять. */
  readOnlyIn: (projectId: string) => boolean;
  /** Уровень записи реестра доступов из назначений; администратор — везде 'edit'. */
  levelOf: (code: string) => AccessLevel;
  /** Есть ли вообще доступ к записи (уровень выше 'none'). */
  hasAccess: (code: string) => boolean;
  /** Что доступно с телефона: возможности и их поведение (server/src/capabilities.ts). */
  mobile: CapabilityState[];
  /** Поведение одной возможности на телефоне. */
  mobileMode: (code: string) => MobileMode;
  /** Видит ли человек все работы функции: охват «все» на её разделе, свой у каждой функции. */
  seesAllOf: (fn: FunctionName) => boolean;
  bootstrap: Bootstrap;
  procMap: ReturnType<typeof byCode>;
  roleIndex: RoleIndex;
  view: View;
  showView: (v: View) => void;
  /** К корню текущего раздела — тап по текущей вкладке полосы. */
  resetSection: () => void;
  /** Страница базы знаний поверх её главной. */
  openKbPage: (page: KbPage) => void;
  /** Встреча, которую раздел «Календарь» открывает карточкой при заходе —
   * клик по уведомлению или ссылка /calendar/event/<id>. Переходное: до
   * этапа календаря карточка не слой, а состояние экрана. */
  calTarget: { id: string; occurrence?: string } | null;
  openCalendarEvent: (eventId: string, occurrence?: string) => void;
  /** Карточка подрядчика, а если дано — сразу проверка внутри неё. */
  openContractor: (id: string, checkId?: string) => void;
  /**
   * Карточка подрядчика сразу на вкладке «Договоры», с открытым договором.
   * Вкладка договора — та, на которой человеку и нужно оказаться: задание
   * ведёт на процессную вкладку (3.60), ссылка из реестра — на реквизиты.
   */
  /** Договор подрядчика — в его карточке; `null` — договор без подрядчика, в «Реестре договоров». */
  openContract: (contractorId: string | null, contractId: string, tab?: ContractTab) => void;
  /** Форма нового договора в «Реестре договоров»: контрагент `orgId`, заготовка — договор функции здания. */
  openNewDeal: (orgId: string, preset?: DealPreset, workId?: string) => void;
  /**
   * Страница нашего юрлица в «Наших юрлицах»: компания — её карточка (заводится, если её ещё нет),
   * фонд — своя. Карточку завести не удалось — обещание отклоняется: показать причину должен вызвавший.
   */
  openOurEntity: (orgId: string, form: 'ORG' | 'FUND', tab: 'docs') => Promise<void>;
  /** Реестр платёжного дня в «Финансах»: из задания «Согласовать / Утвердить реестр». */
  openRegister: (id: string) => void;
  /** Звонок, чьи итоги открыты окном «Итоги звонка» — клик по уведомлению
   * «Итоги звонка» (Bell.tsx, openNotification.ts). */
  summaryCallId: string | null;
  openCallSummary: (callId: string) => void;
  closeCallSummary: () => void;
  projectId: string | null;
  projSection: ProjSection;
  openProject: (id: string, section?: ProjSection) => void;
  showProjSection: (s: ProjSection) => void;
  openProc: (code: string, targetCommentId?: number) => void;
  exitProc: () => void;
  fullRoles: boolean;
  setFullRoles: (v: boolean) => void;
  fullTerms: boolean;
  setFullTerms: (v: boolean) => void;
  logout: () => void;
  /** Выйти без запроса на сервер: сессию уже закрыли (сервер, соседняя вкладка). */
  endSession: (reason: OutReason) => void;
  refreshBootstrap: () => void;
  /** Перечитать свой профиль — после правок в личном кабинете. */
  refreshProfile: () => Promise<void>;
  /**
   * Перечитать профиль по событию доступов. Окна пин-кода не будет, даже если
   * пин-кода на сервере уже нет: оно заменило бы приложение целиком и
   * оборвало идущий звонок (итоговое ревью спеки 2026-10-02-tabs-devices-sync, пункт I3).
   */
  refreshAccess: () => Promise<void>;
};

/** Код записи реестра доступов, которым закрыт раздел меню/полосы. */
export const SECTION_CODE: Partial<Record<View, string>> = {
  // у «Функций» доступ решает viewAllowed; код — для телефона: там живёт только генподряд
  projects: 'section.projects',
  calendar: 'block.calendar',
  chat: 'block.chat',
  contracts: 'section.contracts',
  legal: 'section.admin.legal',
  finance: 'section.finance',
  tasks: 'section.contractors.tasks',
  kb: 'section.kb',
  profile: 'block.profile',
};

/** Разделы функций: пункт «Функции» открыт, если открыт хоть один (спека 2026-09-30). */
export const FUNCTION_CODES: string[] = FUNCTION_ORDER_IN_MENU.map((f) => FUNCTION_SECTION[f]);

/**
 * Открыт ли раздел меню по назначениям. «Функции» — группировка экрана, а
 * не запись реестра (как «Администрирование»): пункт виден, если открыт хоть
 * один из пяти разделов функций; на телефоне из них живёт только генподряд,
 * поэтому там решает он. Остальные разделы — по своей записи, у Главной
 * кода нет.
 */
export function viewAllowed(has: (code: string) => boolean, v: View, mobile = false): boolean {
  if (v === 'projects') return mobile ? has('section.projects') : FUNCTION_CODES.some((c) => has(c));
  const code = SECTION_CODE[v];
  return !code || has(code);
}

/*
 * Хотя бы одна из этих записей должна быть доступна, чтобы показать
 * «Администрирование». Список собирается из самих вкладок админки, а не
 * ведётся рядом руками: пока он был отдельным, в нём недоставало пяти
 * разделов из одиннадцати, и должность, которой открыты только «Юрлица»,
 * пункта меню не видела вовсе (19.09.2026).
 *
 * Оргструктура здесь именно административная: служебное чтение справочника
 * должностей (section.kb.org) открыто всем и админки не даёт (13.09.2026).
 */
export { ADMIN_SECTION_CODES as ADMIN_ACCESS_CODES } from './nav/adminTabs';

/**
 * Порядок увода при прямом заходе в недоступный раздел или отказе сервера
 * (решение Ильи 12.09.2026): раздел не объясняет, что закрыт, а просто
 * подставляет первый из доступных. Чат и профиль неотключаемы, поэтому
 * список всегда даёт результат.
 */
const FALLBACK_ORDER: View[] = ['chat', 'calendar', 'projects', 'contracts', 'kb', 'profile'];

/**
 * Куда уводить при прямом заходе в недоступный раздел или отказе сервера.
 * На компьютере — Главная (решение Ильи 17.09.2026). На телефоне Главной в
 * полосе нет, поэтому первый доступный раздел по порядку; чат и профиль
 * неотключаемы, список всегда даёт результат.
 */
export function firstAccessibleView(app: AppCtx, mobile = false): View {
  if (!mobile) return 'home';
  const shown = mobileModules(app.mobile);
  return FALLBACK_ORDER.find((v) => viewAllowed(app.hasAccess, v, true) && shown.includes(v)) ?? 'profile';
}

/** Отказ сервера по доступу — уводим тем же путём, что и при прямом заходе. */
export function isForbidden(e: unknown): boolean {
  return e instanceof ApiError && e.status === 403;
}

// Экспортирован (не только useApp) ради стенда предпросмотра (web/src/preview/) —
// там нет входа/бутстрапа, и он подставляет своё значение контекста напрямую
// через <Ctx.Provider>, минуя App(). Поведение самого приложения не меняет.
export const Ctx = createContext<AppCtx | null>(null);
export const useApp = () => useContext(Ctx)!;

export function App() {
  // ссылка на комнату звонка: /room/<код>
  const roomToken = useMemo(() => {
    const m = window.location.pathname.match(/^\/room\/([A-Za-z0-9_-]+)$/);
    return m ? m[1] : null;
  }, []);

  // гостевая ссылка на договор: /g/c/<токен>
  const contractToken = useMemo(() => {
    const m = window.location.pathname.match(/^\/g\/c\/([A-Za-z0-9_-]+)$/);
    return m ? m[1] : null;
  }, []);

  // ссылка из письма-приглашения к документу: /g/p/<ссылка>
  const passToken = useMemo(() => {
    const m = window.location.pathname.match(/^\/g\/p\/([A-Za-z0-9_-]+)$/);
    return m ? m[1] : null;
  }, []);

  // ссылка из письма-приглашения: ?invite=токен
  const inviteToken = useMemo(() => new URLSearchParams(window.location.search).get('invite'), []);

  // Стопка экранов и адрес — один контроллер на приложение (nav/nav.ts).
  // Страницы гостя живут по своим адресам (/room/<код>, /g/c/<токен>):
  // контроллер их не пишет, но стопка ему всё равно нужна — на телефоне
  // ветка замечания открывается слоем с записью в истории.
  const navRef = useRef<ReturnType<typeof initNav> | null>(null);
  if (!navRef.current) {
    const outside = roomToken || contractToken || passToken;
    navRef.current = initNav(window.history, outside ? null : window.location.pathname, window.location.search);
  }
  const nav = useNav();
  const view = nav.section;
  const projectLayerNow = view === 'projects'
    ? (nav.layers.find((l) => l.kind === 'project') as Extract<ProjectsLayer, { kind: 'project' }> | undefined)
    : undefined;
  const eventLayerNow = view === 'calendar'
    ? (nav.layers.find((l) => l.kind === 'event') as CalendarLayer | undefined)
    : undefined;

  const [profile, setProfile] = useState<Profile | null>(null);
  const user = profile?.user ?? null;
  // о конце сессии сообщает слой запросов, а он о состоянии экрана не знает:
  // свежий признак «вошли» держим ссылкой
  const userRef = useRef(user);
  userRef.current = user;
  // Кто вошёл — без самого объекта профиля. Перечитанный профиль (новые
  // доступы, правки в кабинете) приносит новый объект того же человека; эффекты
  // «вошёл / вышел» на нём не должны перезапускаться: иначе база знаний
  // грузится заново, а отсчёт тишины начинается сначала во всех вкладках.
  const userId = user?.id ?? null;
  const [checked, setChecked] = useState(false);
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  // база знаний не доехала: без неё не строится контекст и не рисуется ни
  // один раздел, поэтому неудача обязана быть видимой, а не молчаливой
  const [bootFailed, setBootFailed] = useState(false);
  // ожидание затянулось: запрос ушёл на повторы
  const [bootSlow, setBootSlow] = useState(false);

  const [fullRoles, setFullRoles] = useState(() => localStorage.getItem('code-roles-full') !== '0');
  const [fullTerms, setFullTerms] = useState(() => localStorage.getItem('code-terms-full') === '1');
  // предложение поставить пин показывается один раз за вход
  const [pinAsked, setPinAsked] = useState(false);
  // почему вышли: надпись на окне входа (спека 2026-10-02-tabs-devices-sync)
  const [outReason, setOutReason] = useState<OutReason>(null);
  const [summaryCallId, setSummaryCallId] = useState<string | null>(null);
  const restoredAtBoot = useRef(false);

  useEffect(() => {
    api
      .me()
      .then((p) => {
        // сессия пережила перезапуск — в приложении iPhone сперва Face ID
        restoredAtBoot.current = true;
        setProfile(p);
      })
      .catch(() => setProfile(null))
      .finally(() => setChecked(true));
  }, []);

  const loadBootstrap = useCallback(() => {
    setBootFailed(false);
    api
      .bootstrap()
      .then(setBootstrap)
      .catch(() => setBootFailed(true));
  }, []);

  useEffect(() => {
    if (userId) loadBootstrap();
  }, [userId, loadBootstrap]);

  // Затянувшееся ожидание не выдаём за нормальную загрузку.
  useEffect(() => {
    setBootSlow(false);
    if (!userId || bootstrap || bootFailed) return;
    const timer = setTimeout(() => setBootSlow(true), BOOT_SLOW_MS);
    return () => clearTimeout(timer);
  }, [userId, bootstrap, bootFailed]);

  const top = () => window.scrollTo({ top: 0 });

  const showView = useCallback((v: View) => {
    nav.go(v);
    top();
  }, [nav]);

  const resetSection = useCallback(() => {
    nav.reset();
    top();
  }, [nav]);

  const openKbPage = useCallback((page: KbPage) => {
    if (nav.section === 'kb') nav.push(kbPageLayer(page));
    else nav.go('kb', [kbPageLayer(page)]);
    top();
  }, [nav]);

  const openProc = useCallback((code: string, targetCommentId?: number) => {
    const layer = procLayer(code, targetCommentId);
    if (nav.section === 'kb') nav.push(layer);
    else nav.go('kb', [layer]);
    top();
  }, [nav]);

  const exitProc = useCallback(() => nav.back(), [nav]);

  const openProject = useCallback((id: string, section: ProjSection = 'overview') => {
    nav.go('projects', [projectLayer(id, section)]);
    top();
  }, [nav]);

  // Подраздел проекта — поле слоя проекта: тот же ключ, значит замена без
  // новой записи в истории, меняется только адрес.
  const showProjSection = useCallback((s: ProjSection) => {
    const p = nav.layers.find((l) => l.kind === 'project') as Extract<ProjectsLayer, { kind: 'project' }> | undefined;
    if (p) nav.push(projectLayer(p.id, s));
    top();
  }, [nav]);

  const openCalendarEvent = useCallback((eventId: string, occurrence?: string) => {
    nav.go('calendar', [eventLayer(eventId, occurrence)]);
    top();
  }, [nav]);

  /* Переход из задания: карточка подрядчика, а если задание про проверку —
     сразу сама проверка. Человек шёл делать работу, а не искать её. */
  const openContractor = useCallback((id: string, checkId?: string) => {
    nav.go(
      'contracts',
      checkId
        ? [subLayer('counterparties'), cardLayer(id), checkLayer(id, checkId)]
        : [subLayer('counterparties'), cardLayer(id)],
    );
    top();
  }, [nav]);

  /*
   * Вкладку договора не называем за человека (разбор 22.09.2026). Здесь стояло
   * умолчание `tab = 'terms'`, и оно перебивало `landingTab`: правило «договор
   * открывается согласованием, пока идёт работа» жило в карточке договора, а
   * переход из уведомления, из соседнего договора и из заведённого
   * допсоглашения приносил вкладку «Договор» явно — и карточке выбирать было
   * уже нечего. Пустая вкладка значит «не названа», и выберет её сам договор
   * по своему статусу; названную (`'approval'` из задания) по-прежнему
   * открываем ту, которую назвали.
   */
  const openContract = useCallback((contractorId: string | null, contractId: string, tab?: ContractTab) => {
    nav.go('contracts', contractStack(contractorId, contractId, tab));
    top();
  }, [nav]);

  const openNewDeal = useCallback((orgId: string, preset?: DealPreset, workId?: string) => {
    nav.go('contracts', [subLayer('registry'), newDealLayer(orgId, '', '', '', preset, workId)]);
    top();
  }, [nav]);

  /* Страница нашего юрлица. Компании карточку заводит `ensureCompanyCard`, как плитка раздела. Не вышло —
     отказ летит вызвавшему: он стоит внутри ConfirmHost и показывает причину окном, а стопка остаётся как была. */
  const openOurEntity = useCallback(async (orgId: string, form: 'ORG' | 'FUND', tab: 'docs') => {
    if (form === 'FUND') {
      nav.go('legal', [legalFundLayer(orgId, tab)]);
      top();
      return;
    }
    const { profileId } = await api.ensureCompanyCard(orgId);
    nav.go('legal', [legalCardLayer(profileId, tab)]);
    top();
  }, [nav]);

  const openRegister = useCallback((id: string) => {
    nav.go('finance', [registerLayer(id)]);
    top();
  }, [nav]);

  const openCallSummary = useCallback((callId: string) => setSummaryCallId(callId), []);
  const closeCallSummary = useCallback(() => setSummaryCallId(null), []);

  /*
   * Окно входа: профиль и база знаний сбрасываются, причина — на окно. Раздел
   * сбрасывается на Главную, кроме истёкшей сессии: тогда человек входит снова
   * и возвращается туда, где был (так было и до общих вкладок). Открытые итоги
   * звонка закрываются: войти может уже другой человек.
   */
  const endSession = useCallback(
    (reason: OutReason) => {
      setOutReason(reason);
      setProfile(null);
      setBootstrap(null);
      setSummaryCallId(null);
      if (reason !== 'expired') nav.go('home');
    },
    [nav],
  );

  /*
   * Выход из этой вкладки. Соседние вкладки браузера выходят вместе с ней:
   * сессия у них общая (спека 2026-10-02-tabs-devices-sync, раздел 1).
   */
  const logout = useCallback(
    (reason: OutReason = null) => {
      tellTabs({ type: 'logout', reason });
      // явный выход стирает с устройства неотправленное чата; истёкшая сессия
      // и выход по бездействию — нет: вернётся тот же человек
      // стираем, не дожидаясь: зависшее хранилище выход держать не должно
      if (reason === null && userRef.current) void forgetChatDevice(userRef.current.id);
      api.logout().finally(() => endSession(reason));
    },
    [endSession],
  );

  /*
   * Автовыход по бездействию: 15 минут без действий — сессия закрывается.
   * Возврат в приложение — по пин-коду (быстро) или логином с паролем.
   */
  useEffect(() => {
    // в приложении iPhone вместо выхода по бездействию — шторка Face ID (native/AppLock)
    if (!userId || isApp()) return;
    return watchIdle(IDLE_LOGOUT_MS, () => logout('idle'));
  }, [userId, logout]);

  // слушатели ниже подписываются один раз, а выход им нужен свежий
  const endSessionRef = useRef(endSession);
  endSessionRef.current = endSession;

  /*
   * Глобальная реакция на 401: разлогин при протухшей сессии.
   *
   * Слушать `unhandledrejection`, как было раньше, мало: экран со своим
   * `catch` (лента чата) отказ перехватывает, наружу он не всплывает, и
   * человек остаётся в приложении, где не работает ни один запрос. Поэтому
   * о конце сессии сообщает сам слой запросов (`onSessionExpired` в api.ts),
   * независимо от того, поймал ли кто-то ошибку.
   *
   * Пока не вошли (проверка `me` при запуске), выходить неоткуда: экран
   * входа и так впереди.
   *
   * Сессию закрыл сервер, пока вкладка спала, — причина пришла в отказе, и
   * выход идёт так же, как по событию `session` (спека
   * 2026-10-02-tabs-devices-sync, раздел 4): своя надпись, а при смене пароля
   * запомненный вход по пин-коду забывается. То же при запуске: телефон
   * выгрузил приложение в фоне и открыл заново, а отказ стартовой проверки
   * несёт причину — надпись на окно входа. Соседям не говорим: их запросы
   * получат свой отказ.
   */
  const checkedRef = useRef(checked);
  checkedRef.current = checked;
  useEffect(() => {
    onSessionExpired((reason) => {
      if (!userRef.current) {
        if (reason && !checkedRef.current) {
          if (reason === 'password') forgetPinLogin();
          setOutReason(reason);
        }
        return;
      }
      if (reason === 'password') forgetPinLogin();
      const why: OutReason = reason ?? 'expired';
      tellTabs({ type: 'logout', reason: why });
      endSessionRef.current(why);
    });
    return () => onSessionExpired(null);
  }, []);

  /*
   * Соседняя вкладка браузера вошла или вышла. Вышла — выходим и мы, той же
   * причиной. Вошла — открываем приложение здесь; вошёл другой человек —
   * перезагружаемся под него: показывать данные одного, когда браузер уже
   * работает от имени другого, нельзя (общий компьютер).
   */
  useEffect(
    () =>
      onTabs((news) => {
        if (news.type === 'logout') {
          // пин-код сброшен вместе с паролем, а запись соседней вкладки в память
          // браузера может дойти позже вести: окно входа открылось бы пин-кодом
          if (news.reason === 'password') forgetPinLogin();
          if (userRef.current) endSessionRef.current(news.reason);
          return;
        }
        if (userRef.current) {
          if (userRef.current.id !== news.userId) window.location.reload();
          return;
        }
        api
          .me()
          .then((p) => {
            setOutReason(null);
            // пин-код предлагает та вкладка, где входили, остальные не повторяют
            setPinAsked(true);
            setProfile(p);
          })
          .catch(() => {});
      }),
    [],
  );

  const ctx = useMemo<AppCtx | null>(() => {
    if (!user || !bootstrap) return null;
    const grants = profile?.grants ?? [];
    const levelOf = (code: string): AccessLevel => accessLevel(user.isAdmin, grants, code);
    return {
      user,
      positions: profile?.positions ?? [],
      can: (permission: string) => (profile?.permissions ?? []).includes(permission),
      isMemberOf: (projectId: string) => (profile?.memberProjectIds ?? []).includes(projectId),
      readOnlyIn: (projectId: string) =>
        !user.isAdmin && !(profile?.memberProjectIds ?? []).includes(projectId),
      levelOf,
      hasAccess: (code: string) => levelOf(code) !== 'none',
      seesAllOf: (fn) => seesAllOfFunction(user.isAdmin, grants, fn),
      mobile: profile?.mobile ?? [],
      mobileMode: (code: string) => modeOf(profile?.mobile ?? [], code),
      bootstrap,
      procMap: byCode(bootstrap),
      roleIndex: buildRoleIndex(bootstrap),
      view,
      showView,
      resetSection,
      openKbPage,
      calTarget: eventLayerNow ? { id: eventLayerNow.id, occurrence: eventLayerNow.at } : null,
      openCalendarEvent,
      summaryCallId,
      openCallSummary,
      closeCallSummary,
      openContractor,
      openContract,
      openNewDeal,
      openOurEntity,
      openRegister,
      projectId: projectLayerNow?.id ?? null,
      projSection: projectLayerNow?.section ?? 'overview',
      openProject,
      showProjSection,
      openProc,
      exitProc,
      fullRoles,
      setFullRoles: (v) => {
        setFullRoles(v);
        localStorage.setItem('code-roles-full', v ? '1' : '0');
      },
      fullTerms,
      setFullTerms: (v) => {
        setFullTerms(v);
        localStorage.setItem('code-terms-full', v ? '1' : '0');
      },
      logout: () => logout(),
      endSession,
      refreshBootstrap: loadBootstrap,
      refreshProfile: async () => {
        const fresh = await api.me().catch(() => null);
        if (fresh) setProfile(fresh);
      },
      refreshAccess: async () => {
        const fresh = await api.me().catch(() => null);
        if (!fresh) return;
        setPinAsked(true);
        setProfile(fresh);
      },
    };
  }, [
    user, profile, bootstrap, view, nav.version, showView, resetSection, openKbPage, openProc, exitProc,
    fullRoles, fullTerms, logout, endSession, loadBootstrap, projectLayerNow, eventLayerNow,
    openProject, showProjSection, openCalendarEvent,
    summaryCallId, openCallSummary, closeCallSummary, openContractor, openContract, openNewDeal, openOurEntity, openRegister,
  ]);

  if (roomToken) return <GuestRoom token={roomToken} />;
  // Договор по ссылке: вне оболочки — ни меню, ни полосы вкладок, ни колокольчика.
  if (contractToken) return <GuestContract token={contractToken} />;
  if (passToken) return <GuestPass pass={passToken} go={goReplace} />;

  if (inviteToken && !user) {
    return (
      <InviteAccept
        token={inviteToken}
        onDone={() => {
          // Контроллер на адресе приглашения адрес не вёл (routes.placeFrom):
          // перезапускаем приложение с корня, сессия уже в cookie.
          window.location.replace('/');
        }}
      />
    );
  }

  if (!checked) return <div className="load-screen">Загрузка…</div>;
  if (!user) {
    return (
      <Login
        notice={outReason ? OUT_NOTICE[outReason] : undefined}
        onLogin={(p) => {
          tellTabs({ type: 'login', userId: p.user.id });
          setOutReason(null);
          setPinAsked(false);
          setProfile(p);
        }}
      />
    );
  }
  // после входа по паролю предлагаем быстрый вход, если пина ещё нет
  if (!user.hasPin && !pinAsked && !pinOfferSkipped(user.login)) {
    return (
      <PinSetup
        user={user}
        onDone={(u) => {
          setPinAsked(true);
          setProfile((p) => (p ? { ...p, user: u } : p));
        }}
        onSkip={() => setPinAsked(true)}
      />
    );
  }
  if (!ctx) {
    if (bootFailed) {
      return (
        <div className="load-screen load-screen-fail">
          <p>Не удалось загрузить базу знаний.</p>
          <button type="button" className="btn primary" onClick={loadBootstrap}>
            Повторить
          </button>
        </div>
      );
    }
    if (bootSlow) {
      return (
        <div className="load-screen load-screen-fail">
          <p>Сервер не отвечает. Пробуем ещё раз…</p>
        </div>
      );
    }
    return <div className="load-screen">Загружаю базу знаний…</div>;
  }

  return (
    <Ctx.Provider value={ctx}>
      <ConfirmHost>
        <Shell />
      </ConfirmHost>
      {isApp() && <AppLock startLocked={restoredAtBoot.current} onLogout={() => logout()} />}
    </Ctx.Provider>
  );
}
